import { Container, Flex, Heading, Stack, useBreakpointValue } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { PortalButton } from 'components/buttons/PortalButton';
import RequirementsChecklist from 'components/RequirementsChecklist';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { RiLockPasswordLine } from 'react-icons/ri';
import * as yup from 'yup';
import YupPassword from 'yup-password';

import { PasswordField } from '../../components/PasswordField';
import useUpdateUserPasswordMutation from '../../hooks/mutations/useUpdateUserPasswordMutation';
import { ContentCard } from '../../layout/ContentCard';

YupPassword(yup);
export interface PasswordConfirmForm {
  password: string;
  password_confirmation: string;
  token: string;
}
const UserPasswordCard = (props: { token: string }) => {
  const { token } = props;

  const updateUserPasswordMutation = useUpdateUserPasswordMutation();
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const { t } = useTranslation();
  const schema = yup
    .object({
      password: yup
        .string()
        .min(8, t('forms:password.length'))
        .minNumbers(1, t('forms:password.number'))
        .minUppercase(1, t('forms:password.uppercase'))
        .minSymbols(1, t('forms:password.symbols'))
        .required(),
      password_confirmation: yup
        .string()
        // @ts-expect-error
        .oneOf([yup.ref('password'), null], t('forms:password.match'))
        .required(t('forms:password.length')),
      token: yup.mixed(),
    })
    .required();

  const createValidationMap = (): Map<string, string> => {
    const validationConfig = {
      min: t('forms:password.criteria.length'),
      minNumber: t('forms:password.criteria.number'),
      minSymbol: t('forms:password.criteria.symbols'),
      minUppercase: t('forms:password.criteria.uppercase'),
    };
    return new Map(Object.entries(validationConfig));
  };
  const validationMap: Map<string, string> = createValidationMap();

  const {
    register,
    watch,
    setValue,
    formState: { errors, isValid, submitCount },
    handleSubmit,
  } = useForm({
    defaultValues: {
      password: password,
      password_confirmation: passwordConfirmation,
      token: token,
    },
    resolver: yupResolver(schema),
    criteriaMode: 'all',
    mode: 'onChange',
  });
  const selectedPassword = watch('password');
  const selectedPasswordConfirmation = watch('password_confirmation');
  useEffect(() => {
    setPassword(password);
    setPasswordConfirmation(passwordConfirmation);
    setValue('token', token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPassword, selectedPasswordConfirmation]);

  const onSubmit: SubmitHandler<yup.InferType<typeof schema>> = (data) => {
    // @ts-expect-error
    updateUserPasswordMutation.mutate({ token: token, userData: data });
  };

  return (
    <ContentCard icon={<RiLockPasswordLine size={'40'} />} minH="fit-content" header={t('forms:password.label')}>
      <Container
        as="form"
        onSubmit={handleSubmit(onSubmit)}
        data-test-id="set-password-form"
        py={{ base: '0', sm: '8' }}
        px={{ base: '4', sm: '6' }}
        // @ts-ignore
        autoComplete="new-password"
      >
        <Stack spacing="8" alignItems={'center'}>
          <Stack spacing="8" w={{ base: 'full', lg: '30rem' }}>
            <PasswordField data-lpignore={'true'} label={t('forms:password.label')} register={register('password')} />
            <PasswordField
              data-lpignore={'true'}
              label={t('forms:password.password_confirmation.label')}
              register={register('password_confirmation')}
              // @ts-ignore
              error={errors.password_confirmation}
            />
            <Stack spacing={3} textAlign={'left'}>
              <Heading as="h2" size={useBreakpointValue({ base: 'xxs', md: 'xxs' })}>
                {t('forms:password.criteria.label')}
              </Heading>
              <RequirementsChecklist
                validationMap={validationMap}
                // @ts-ignore
                error={errors.password}
              />
            </Stack>
          </Stack>
        </Stack>
        <Flex direction="row-reverse" py="4" px={{ base: '4', md: '6' }}>
          <PortalButton
            type="submit"
            data-test-id="set-password-next-button"
            disabled={(submitCount! > 0 && !isValid) || updateUserPasswordMutation.isPending}
          >
            {t('common:submit')}
          </PortalButton>
        </Flex>
      </Container>
    </ContentCard>
  );
};

export default UserPasswordCard;
